// generationAdapter — the boundary parsers for the generation job (phase 2c).
// Pure: `unknown` wire JSON in, typed value or a Malformed error out (parse,
// don't validate). The job snapshot is parsed with the server's own zod schema
// (`@bilda/app/generationSnapshot`, the `showGenerationJob` output contract),
// so client and server cannot drift. The skeleton is read off `showResource`'s
// `view.materialView.parts` with the cast-free wire readers (no zod schema
// exists for the material view; its phase 3 fields are read the same way).

export { parseGenerationJob, parseGenerationSkeleton, parseGenerationSnapshot };

import { generationJobSnapshotDto } from "@bilda/app/generationSnapshot";
import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type {
    GenerationJobSnapshot,
    GenerationSkeleton,
    NewerVersions,
    PartDependency,
    PartReview,
    SkeletonPart,
    SkeletonPartStatus,
    StaleReason,
} from "./ports/generation.js";
import { fields, list, num, str } from "./wireShape.js";

const parseGenerationSnapshot = (
    value: unknown,
): Result.Result<GenerationJobSnapshot, ExternalError> => {
    const parsed = generationJobSnapshotDto.safeParse(value);
    return parsed.success
        ? Result.ok(parsed.data)
        : Result.err(
              mkMalformed(`generation snapshot: ${parsed.error.message}`),
          );
};

// `showGenerationJob` answers a snapshot or `null` (no job).
const parseGenerationJob = (
    value: unknown,
): Result.Result<GenerationJobSnapshot | null, ExternalError> =>
    value === null ? Result.ok(null) : parseGenerationSnapshot(value);

const toStatus = (v: unknown): SkeletonPartStatus | undefined => {
    switch (v) {
        case "planned":
        case "written":
        case "removed":
            return v;
        default:
            return undefined;
    }
};

// Version lists: a non-number entry is dropped, never a crash.
const versions = (v: unknown): readonly number[] =>
    list(v).flatMap((x) => {
        const n = num(x);
        return n === undefined ? [] : [n];
    });

const toDependency = (v: unknown): readonly PartDependency[] => {
    const f = fields(v);
    const id = str(f?.get("id"));
    const title = str(f?.get("title"));
    const version = num(f?.get("version"));
    return id === undefined || title === undefined || version === undefined
        ? []
        : [{ id, title, version }];
};

const toStaleReason = (v: unknown): readonly StaleReason[] => {
    const f = fields(v);
    const dependency = str(f?.get("dependency"));
    const dependencyTitle = str(f?.get("dependencyTitle"));
    const pinned = num(f?.get("pinned"));
    return dependency === undefined ||
        dependencyTitle === undefined ||
        pinned === undefined
        ? []
        : [
              {
                  dependency,
                  dependencyTitle,
                  pinned,
                  heads: versions(f?.get("heads")),
              },
          ];
};

const toPolicy = (v: unknown): NewerVersions["policy"] | undefined => {
    switch (v) {
        case "follow":
        case "ask":
            return v;
        default:
            return undefined;
    }
};

const toNewerVersions = (v: unknown): NewerVersions | null => {
    const f = fields(v);
    const refId = str(f?.get("refId"));
    const pinned = num(f?.get("pinned"));
    const policy = toPolicy(f?.get("policy"));
    const heads = versions(f?.get("heads"));
    return refId === undefined ||
        pinned === undefined ||
        policy === undefined ||
        heads.length === 0
        ? null
        : { refId, pinned, heads, policy };
};

const toReviewStatus = (v: unknown): PartReview["status"] | undefined => {
    switch (v) {
        case "unreviewed":
        case "reviewed":
        case "stale_after_review":
            return v;
        default:
            return undefined;
    }
};

const toReview = (v: unknown): PartReview | null => {
    const f = fields(v);
    const status = toReviewStatus(f?.get("status"));
    const head = num(f?.get("head"));
    return status === undefined || head === undefined
        ? null
        : { status, head, approved: num(f?.get("approved")) ?? null };
};

// Phase 3 and 5a fields are additive: an older server's part reads as fresh, with no
// dependencies and no newer versions.
const toPart = (v: unknown): readonly SkeletonPart[] => {
    const f = fields(v);
    const id = str(f?.get("id"));
    const title = str(f?.get("title"));
    const status = toStatus(f?.get("status"));
    return id === undefined || title === undefined || status === undefined
        ? []
        : [
              {
                  id,
                  title,
                  status,
                  buildsOn: list(f?.get("buildsOn")).flatMap(toDependency),
                  stale: list(f?.get("stale")).flatMap(toStaleReason),
                  newerVersions: toNewerVersions(f?.get("newerVersions")),
                  review: toReview(f?.get("review")),
              },
          ];
};

// `showResource` (material) → the root's title + included parts. A material
// view without `parts` (an older server) reads as no parts.
const parseGenerationSkeleton = (
    value: unknown,
): Result.Result<GenerationSkeleton, ExternalError> => {
    const view = fields(fields(value)?.get("view"));
    const title = str(fields(view?.get("resource"))?.get("title"));
    const materialView = fields(view?.get("materialView"));
    if (title === undefined || materialView === undefined) {
        return Result.err(mkMalformed("showResource: not a material view"));
    }
    return Result.ok({
        title,
        parts: list(materialView.get("parts")).flatMap(toPart),
    });
};
